import classNames from 'classnames';

import { TypographyAlign, TypographyClassName, TypographyComponentType, TypographyVariant } from './typography.types';

export const TYPOGRAPHY_VARIANT_LOOKUP: { [key in TypographyVariant]: TypographyClassName } = {
  none: undefined,
  default: 'font-normal',
  primary: 'font-bold',
  secondary: 'font-normal',
  label: 'font-medium uppercase',
  section: 'font-medium leading-tight',
  'section-sm': 'font-medium leading-tight font-medium uppercase',
  sub: 'font-normal leading-tight',
  info: 'font-normal italic',
  h2: 'font-bold tracking-normal',
  h3: 'font-normal tracking-normal',
  dt: 'font-normal',
  dd: 'font-medium',
  caption: '',
};

export const TYPOGRAPHY_VARIANT_FONT_SIZE_LOOKUP: { [key in TypographyVariant]: TypographyClassName } = {
  none: undefined,
  default: 'text-base',
  primary: 'text-sm leading-normal',
  secondary: 'text-sm',
  label: 'text-xs',
  section: 'text-lg',
  'section-sm': 'text-xs',
  sub: 'text-xs',
  info: 'text-base leading-6',
  h2: 'text-xl md:text-3xl',
  h3: 'text-xs md:text-sm',
  dt: 'text-sm',
  dd: 'text-sm',
  caption: '',
};

export const TYPOGRAPHY_ALIGN_LOOKUP: { [key in TypographyAlign]: TypographyClassName } = {
  inherit: '[text-align:inherit]',
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
  justify: 'text-justify',
};

// React.ElementType or ComponentType
export const TYPOGRAPHY_COMPONENT_VARIANT_LOOKUP: { [key in TypographyVariant]: TypographyComponentType } = {
  none: 'p',
  default: 'p',
  primary: 'span',
  secondary: 'span',
  label: 'span',
  caption: 'span',
  section: 'div',
  'section-sm': 'div',
  sub: 'span',
  info: 'div',
  h2: 'h2',
  h3: 'h3',
  dt: 'dt',
  dd: 'dd',
};

export interface TypographyProps {
  className?: string;
  as?: TypographyComponentType;
  variant?: TypographyVariant;
  variantComponent?: TypographyVariant;
  variantFontSize?: TypographyVariant;
  variantColor?: TypographyVariant;
  align?: TypographyAlign;
  gutterBottom?: boolean;
  noWrap?: boolean;
  paragraph?: boolean;
}

export const TYPOGRAPHY_VARIANT_COLOR_LOOKUP: { [key in TypographyVariant]: TypographyClassName } = {
  none: undefined,
  default: undefined,
  primary: 'text-white dark:text-gray-100',
  secondary: 'text-white dark:text-gray-300',
  label: 'text-white dark:text-gray-300',
  section: 'text-white dark:text-white',
  'section-sm': 'text-secondary dark:text-white',
  sub: 'text-secondary dark:text-gray-400',
  info: 'text-secondary dark:text-gray-400',
  h2: 'text-black dark:text-white',
  h3: 'text-neutral-600 dark:text-neutral-400',
  dt: 'text-white',
  dd: 'text-white',
  caption: undefined,
};

/**
 * Present text in a predefined and reusable way
 */
export function Typography(props: React.PropsWithChildren<TypographyProps>) {
  const {
    children,
    className,
    as,
    variant = 'default',
    variantComponent = variant,
    variantFontSize = variant,
    variantColor = variant,
    align = 'inherit',
    gutterBottom,
    noWrap,
    paragraph,
  } = props;

  const Component: TypographyComponentType =
    as || (paragraph ? 'p' : TYPOGRAPHY_COMPONENT_VARIANT_LOOKUP[variantComponent]) || 'span';

  const spanClass: TypographyClassName = Component === 'span' ? 'block' : undefined; // span elements need to have `display: block`
  const paragraphClass: TypographyClassName = paragraph ? 'mb-4' : undefined;
  const gutterBottomClass: TypographyClassName = gutterBottom && !paragraph ? 'mb-1' : undefined;
  const noWrapClass: TypographyClassName = noWrap ? 'overflow-hidden text-ellipsis whitespace-nowrap' : undefined;
  const alignClass = variant ? TYPOGRAPHY_ALIGN_LOOKUP[align] : undefined;
  const variantClass = variant ? TYPOGRAPHY_VARIANT_LOOKUP[variant] : undefined;
  const variantFontSizeClass = variantFontSize ? TYPOGRAPHY_VARIANT_FONT_SIZE_LOOKUP[variantFontSize] : undefined;
  const variantColorClass = variantColor ? TYPOGRAPHY_VARIANT_COLOR_LOOKUP[variantColor] : undefined;

  return (
    <Component
      className={classNames(
        spanClass,
        paragraphClass,
        gutterBottomClass,
        noWrapClass,
        alignClass,
        variantClass,
        variantFontSizeClass,
        variantColorClass,
        className,
      )}
    >
      {children}
    </Component>
  );
}

export default Typography;
